import { Component, Input, OnChanges, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { FileDropEntry } from '@app/shared/components/smash-file-drop/file-drop-entry';
import { FileSystemMetadata } from '@app/shared/components/smash-file-drop/interfaces';
import { isSafari } from '@app/shared/helpers/environment';
import { isUploadStatusBusy } from '@app/shared/helpers/uploader';
import { FileReaderError } from '@app/shared/models/fileReaderError';
import { Transfer } from '@app/shared/models/transfer';
import { Store } from '@ngrx/store';
import { Observable, combineLatest } from 'rxjs';
import * as fromUploader from '@app/client/uploader/store/reducers';
import { SmashFileDropComponent } from '@app/shared/components/smash-file-drop/smash-file-drop.component';
import { Identity } from '@app/shared/models/identity';
import { first, map } from 'rxjs/operators';
import * as fromRoot from '@app/reducers';
import { UploaderStatus } from '@smash-sdk/uploader';
import { RawFileStoreService } from '@app/core/services/raw-file-store.service';
import { cleanFilename } from '@app/shared/helpers/transferFile';


interface FileDescription {
  name: string;
  cleanName: string;
  type: 'Folder' | 'File';
  size: number;
  fileNumber?: number;
  extension?: string;
  errors?: FileReaderError[];
}
@Component({
  selector: 'app-upload-files-box',
  templateUrl: './upload-files-box.component.html',
  styleUrls: ['./upload-files-box.component.scss']
})
export class UploadFilesBoxComponent implements OnInit, OnChanges {

  @Input() files: FileSystemMetadata[];
  @Input() totalToUpload: number;

  @ViewChild(SmashFileDropComponent) smashFileDrop: SmashFileDropComponent;

  transfer$: Observable<Transfer>;
  uploadStatus$: Observable<UploaderStatus>;
  identity$: Observable<Identity>;
  updateAndResumeUploadLoading$: Observable<boolean>;
  modalSizePremiumDisplayed$: Observable<boolean>;
  // Drives the box's '.animate' position-shift class (slides the box out of the way). Must stay in
  // lockstep with upload-parameters-box's own isUploadAnimating$: both boxes share the same
  // prepare-subcontainer row, so if one slides on a status/guard this one doesn't recognise, they
  // slide out of sync and overlap instead of making room for each other. Before this fix this box
  // used a standalone `uploadStatus$ === 'Starting'` check with no modalSizePremiumDisplayed$ guard,
  // so a transfer becoming Queued (which flips upload-parameters-box's isUploadAnimating$ but not
  // this raw check) left the two boxes visibly colliding while the 2GB queue modal was up.
  isUploadAnimating$: Observable<boolean>;

  addFilesDropdownToggled = false;
  fileDescriptions: FileDescription[] = [];
  fileDescriptionsDic: Map<string, FileDescription> = new Map<string, FileDescription>();
  isSafari: boolean;
  isDirectory: boolean;
  tooltipFileOptions = {
    placement: 'top',
    showDelay: 300
  };

  constructor(
    private store$: Store<fromRoot.State & fromUploader.State>,
    private rawFileStoreService: RawFileStoreService,
  ) {
    this.transfer$ = this.store$.select(fromUploader.getTransfer);
    this.uploadStatus$ = this.store$.select(fromUploader.getUploadStatus);
    this.identity$ = this.store$.select(fromRoot.getActiveIdentity);
    this.updateAndResumeUploadLoading$ = this.store$.select(fromUploader.getUpdateAndResumeUploadLoading);
    this.modalSizePremiumDisplayed$ = this.store$.select(fromUploader.getModalSizePremiumDisplayed);

    this.isUploadAnimating$ = combineLatest([this.uploadStatus$, this.updateAndResumeUploadLoading$, this.modalSizePremiumDisplayed$]).pipe(
      map(([uploadStatus, updateAndResumeUploadLoading, modalSizePremiumDisplayed]) =>
        !modalSizePremiumDisplayed && (isUploadStatusBusy(uploadStatus) || updateAndResumeUploadLoading)),
    );
  }

  ngOnInit(): void {
    this.isSafari = isSafari();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.files) {
      this.updateFileList(this.files);
    }
  }

  // Set by (entryReadError) -- both dropzone instances in this component's template share this
  // flag, captured+reset synchronously at the top of dropped() -- setFiles()'s own resetErrors()
  // would otherwise wipe an error dispatched any earlier, and reading this flag only AFTER
  // awaiting setFiles() would risk a second, overlapping drop's entryReadError overwriting it
  // before this drop's check runs. See onEntryReadError().
  private hadEntryReadError = false;

  onEntryReadError(): void {
    this.hadEntryReadError = true;
  }

  async dropped(files: FileDropEntry[]) {
    const hadEntryReadError = this.hadEntryReadError;
    this.hadEntryReadError = false;
    // tslint:disable-next-line:no-shadowed-variable
    const identity = await this.identity$.pipe(first(identity => !!identity)).toPromise();
    const newFiles = await this.rawFileStoreService.setFiles(files, identity);
    if (hadEntryReadError) {
      this.rawFileStoreService.reportRelativePathTooLongError();
    }
    this.updateFileList(newFiles);
    this.closeDropdownAddFiles();
  }

  onRemoveFile(file: FileDescription) {
    if (file.type === 'Folder') {
      this.rawFileStoreService.removeFolder(file.name);
    } else {
      this.rawFileStoreService.removeFile(file.name);
    }
    this.fileDescriptionsDic.delete(file.name);
    this.fileDescriptions = this.sortFileDescriptions(Array.from(this.fileDescriptionsDic.values()));
  }

  toggleDropdownAddFiles(): void {
    this.addFilesDropdownToggled = !this.addFilesDropdownToggled;
  }

  closeDropdownAddFiles(): void {
    this.addFilesDropdownToggled = false;
  }

  openSelectFiles(target: string) {
    this.isDirectory = target === 'folder' ? true : false;
    setTimeout(() => this.smashFileDrop.openFileSelector(), 0);
  }

  updateFileList(files: FileSystemMetadata[]): void {
    const filteredFiles = files.filter(file => !this.fileDescriptionsDic.has(file.rootFolder) && !this.fileDescriptionsDic.has(file.relativePath));
    if (filteredFiles.length) {
      for (const file of filteredFiles) {

        const name = file.rootFolder ? file.rootFolder : file.relativePath;
        const fileDescriptionFound = this.fileDescriptionsDic.get(name);
        if (fileDescriptionFound) {
          this.fileDescriptionsDic.set(name, {
            ...fileDescriptionFound,
            fileNumber: fileDescriptionFound.fileNumber + 1,
            size: fileDescriptionFound.size + file.size,
            errors: file.error ? [...fileDescriptionFound.errors, file.error] : [...fileDescriptionFound.errors],
          });
        } else {
          const fileDescription: FileDescription = {
            name,
            cleanName: cleanFilename(name),
            size: file.size,
            fileNumber: 1,
            type: file.rootFolder ? 'Folder' : 'File',
            extension: file.extension,
            errors: file.error ? [file.error] : [],
          };
          this.fileDescriptionsDic.set(name, fileDescription);
        }
      }
      this.fileDescriptions = this.sortFileDescriptions(Array.from(this.fileDescriptionsDic.values()));
    }
  }

  private sortFileDescriptions(fileDescriptions: FileDescription[]): FileDescription[] {
    return fileDescriptions.sort((fileDescriptionA, fileDescriptionB) => {
      const errorPriorityDifference = (fileDescriptionB?.errors?.length > 0 ? 1 : 0) - (fileDescriptionA?.errors?.length > 0 ? 1 : 0);
      if (errorPriorityDifference !== 0) {
        return errorPriorityDifference;
      }
      const folderPriority = (fileDescription: FileDescription) => fileDescription.type === 'Folder' ? -1 : 1;
      return folderPriority(fileDescriptionA) - folderPriority(fileDescriptionB);
    });
  }

  get errorsDetected(): any {
    return this.fileDescriptions.some(fileDescription => fileDescription?.errors?.length > 0);
  }
}
